export const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Lfhi54aAAAAAHcQNQNbmd29a54N3h5sNlc9ueS7'
export const isProd = process.env.REACT_APP_ENV === 'PROD'
export const isDev = !isProd && process.env.REACT_APP_ENV === 'DEV'

// Third-party trackers are gated to the DEPLOYED apps, never a developer's
// machine — and an env check alone does not achieve that, because `bun start`
// runs with the same REACT_APP_ENV=DEV the deployed dev build uses. Shared by
// PostHog (monitorToolsSetup) and the HubSpot tracking code.
//
// Kept as a pure predicate, separate from the constant below: that constant is fixed
// at module load from the real location, so no test can flip it.
//
// An EMPTY hostname counts as localhost, and that clause is load-bearing: happy-dom
// serves tests from about:blank, so without it every `bun test` run imports
// monitorToolsSetup, passes the gate, and fires real traffic at the live PostHog and
// HubSpot projects. `file://` and about:blank are not deployed apps either.
//
// IPv6 loopback arrives BRACKETED — `location.hostname` for http://[::1] is `[::1]`,
// and longhand forms (http://[0:0:0:0:0:0:0:1]) normalise to it as well — so the bare
// '::1' never appears via location. It stays listed only because this predicate takes
// an arbitrary string and a caller could hand over an unbracketed host.
export const isLocalHostname = (hostname: string): boolean =>
  !hostname ||
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  hostname === '::1' ||
  hostname === '[::1]' ||
  hostname.endsWith('.local')

export const isLocalhost = isLocalHostname(
  typeof window === 'undefined' ? '' : window.location.hostname,
)
export const PHLO_HOST =
  process.env.REACT_APP_PHLO_HOST || 'https://d2nvdon9sfh3to.cloudfront.net/#/'

export const HOST = isProd ? 'contacto' : 'contactodev'
export const QUEUE_DOMAIN = `https://${isDev ? 'dev-' : '{{region}}-'}queue-execution-service.${HOST}.com/`
export const NANGO_HOST = process.env.REACT_APP_NANGO_HOST || 'http://localhost:3003'
// plivocx host for the ACI integration. Some /v1/aci/* routes (browser OAuth
// callbacks) are not behind hodor-regional, so they're called directly.
// Deliberately NO fallback: the request interceptor attaches the console session
// token to every call, so a bundle that quietly defaults to a loopback host would
// hand that token to whatever happens to listen on the user's machine. Unset =>
// empty => the call site refuses to fire (see requireConfiguredPlivoCxUrl).
// Local dev sets it in apps/web/.env.local.dev.
export const PLIVOCX_URL = process.env.REACT_APP_PLIVOCX_URL || ''

// The ACI bridge's OAuth2 callback URL that operators paste into each provider's developer
// app (Google/Slack/…). It must be the real per-environment bridge host, so there is no
// fallback: the operator screen renders this verbatim under "whitelist this URL", and a
// loopback default would invite adding `http://localhost:…` as a redirect URI on Plivo's
// REAL provider OAuth apps. Unset => the screen shows nothing to copy.
// Local dev sets it in apps/web/.env.local.dev.
export const ACI_BRIDGE_CALLBACK_URL = process.env.REACT_APP_ACI_BRIDGE_CALLBACK_URL || ''

export const TOAST_TIMEOUT = {
  api: 500000,
}
export const COUNTRIES_WITH_MMS = ['US', 'CA']
export const COUNTRIES_WITH_SMS = ['US', 'CA', 'NL', 'AU', 'SE', 'FR', 'BE', 'AT', 'GB']
export const CHAT_WIDGET_HOST =
  process.env.REACT_APP_CHAT_WIDGET_HOST || 'https://chat-widget.contactodev.com'

export const SENTRY_DSN =
  'https://beb44ac71c9ce4fc2ccb7076fcc2e450@o4506437523734528.ingest.sentry.io/4506438492946432'
export const LOG_ROCKET_APP_ID = 'hjxhrz/contacto-console-6cwti'

// Public id, inlined like the others. Loaded from v2/utils/gtm on pre-login page
// loads only, not as an index.html snippet.
export const GTM_CONTAINER_ID = 'GTM-KZ4D8ZMM'

// Excludes a node from BOTH recorders. Name is PostHog's (blocked natively);
// LogRocket is told via dom.privateClassNameBlocklist. One constant so the two
// cannot drift — they were out of sync, which left card fields in LogRocket.
export const PRIVATE_FIELD_CLASS = 'ph-no-capture'

// PostHog project token (public). Prod = "Plivo CX Service" (65498), every other
// env = "Plivo CX stage" (77178). Ingestion is reverse-proxied through f.plivo.com.
export const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY
export const POSTHOG_API_HOST = 'https://f.plivo.com'
export const POSTHOG_UI_HOST = 'https://us.posthog.com'

// HubSpot tracking code portal (js.hs-scripts.com/<portalId>.js). Public,
// browser-shipped ids, so they are inlined like LOG_ROCKET_APP_ID rather than
// wired through .env (which would also need a consul-template entry). One portal
// per environment: deployed-dev traffic would otherwise land in the same reports
// and contact timelines as customers'.
export const HUBSPOT_PORTAL_ID = isProd ? '20451141' : '21976752'

// Fingerprint public key, one per Fingerprint environment, set in the env files.
// The chat widget ships its own copy in apps/chat/src/config/index.ts; rotate both together.
export const FINGERPRINT_PUBLIC_API_KEY = process.env.REACT_APP_FINGERPRINT_PUBLIC_API_KEY

// Set in dev only; empty disables the messenger entirely.
// A getter, not a const: tests flip the env to exercise the configured path, which a
// value baked at import time cannot support. Vite still inlines the literal at build.
export const getIntercomAppId = () => process.env.REACT_APP_INTERCOM_APP_ID || ''

export const VALID_FILE_SIZES_IN_MB = {
  CHAT: '5 MB',
  WHATSAPP: '64 MB',
  MESSAGE: '2 MB',
  COMPLIANCE: '4 MB',
}

export const APPLICATION_FILE_EXTENSIONS = [
  'doc',
  'docx',
  'pdf',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
]

export const HOLD_MUSIC_URL =
  'https://d1bs9qirzh9no.cloudfront.net/42672e6e-c68b-41dc-9b77-7a68a938419b/53f61e44-7421-47b7-b17d-1dd75bdad056.mp3'

export const REACT_FLOW_MIN_ZOOM = 0.3
export const REACT_FLOW_MAX_ZOOM = 2

export const CONTACT_SALES_URL_IN_US = 'https://plivo.cal.com/sales/contact-sales-americas'
export const CONTACT_SALES_URL_OUTSIDE_US = 'https://plivo.cal.com/sales/contact-sales-intl'

export const REGIONS = {
  US: 'us-east-1',
  IN: 'ap-south-1',
}

// Inverse of REGIONS for analytics payloads: AWS region code → ISO-2 country.
// Falls back to the input for legacy/unknown regions rather than dropping the value.
// Lives in constants.ts (a leaf file) so any analytics utility can import it
// without risk of import cycles.
export const regionToCountry = (region: string | undefined | null): string => {
  if (!region) return ''
  for (const [country, awsCode] of Object.entries(REGIONS)) {
    if (awsCode === region) return country
  }
  return region
}

export const ZIP_CODE_VALIDATION_URL = 'https://api.postalpincode.in/pincode'
// A hung lookup blocks Save; past this it fails open instead.
export const ZIP_CODE_VALIDATION_TIMEOUT_MS = 5000

export const VERIFY_APPLICATION_OTP_EXPIRY_TIME = 60

export const PURPOSE_OPTIONS = [
  'Voice AI Agents',
  '2FA',
  'OTP Verifications',
  'Alerts and Notifications',
  'Marketing',
  'Customer Support',
  'Other',
]

export const CHANNEL_OPTIONS = [
  { value: 'Voice', label: 'Voice' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Chat', label: 'Chat' },
  { value: 'SMS', label: 'SMS' },
]

export const getCountryOptions = () =>
  (window.contactoCountryData || []).map(([countryName, iso]) => ({
    label: `${countryName} - ${iso}`,
    value: iso,
  }))
